// Platform admin settings — super-admin only.
//
// First surface: the two post-render vision-QC gates (static vs video).
// Backend is the real authz boundary (`GET`/`PATCH /api/admin/qc-config`
// 403s non-super-admins). This page still gates render so a deep-link
// doesn't sit on a spinner forever, and so a stale JWT `isSuperAdmin`
// hint is reconciled against the live 403.

import { useCallback, useEffect, useState } from 'react';
import {
  Card, CardBody, VStack, HStack, Text, Box, Switch, Skeleton,
  useToast, Badge
} from '@chakra-ui/react';
import { PageHeader } from '../../shell/PageHeader';
import { apiJson, isApiError } from '../../auth/apiFetch';
import { useAuth } from '../../auth/AuthContext';

type GateState = {
  setting: true | false | null;
  effective: boolean;
};

type QcConfig = {
  static: GateState;
  video: GateState;
};

type GateKey = 'static' | 'video';

const GATES: Record<GateKey, { label: string; patchKey: 'staticEnabled' | 'videoEnabled'; description: string }> = {
  static: {
    label: 'Static ad vision QC',
    patchKey: 'staticEnabled',
    // Copy reused from liquidretail_backend/config/defaults.env
    // ("Post-render vision QC on finished static ads…").
    description:
      'Post-render vision QC on finished static ads. Compares the original product photo against the finished render; on fail it regenerates exactly once, then flags and stops. Each check is a billable vision call, and a failed verdict spends one extra image submit. Off ships uninspected.'
  },
  video: {
    label: 'Video ad vision QC',
    patchKey: 'videoEnabled',
    // Copy reused from adVisionQcService.js video-contract header +
    // defaults.env (never regenerates; master already paid).
    description:
      'Post-render vision QC on finished video ads. Compares the original product photo against sampled frames from the delivered clip. Never regenerates — a video master is already paid, and a baked-in defect cannot be fixed by a second submit. Off ships uninspected.'
  }
};

export function AdminSettingsPage() {
  const auth = useAuth();
  const toast = useToast();

  const jwtIsAdmin = auth.status === 'authenticated' && auth.user.isSuperAdmin === true;

  const [config, setConfig]     = useState<QcConfig | null>(null);
  const [loading, setLoading]   = useState(jwtIsAdmin);
  const [forbidden, setForbidden] = useState(!jwtIsAdmin);
  const [error, setError]       = useState<string | null>(null);
  const [busy, setBusy]         = useState<GateKey | null>(null);
  const [rowError, setRowError] = useState<Partial<Record<GateKey, string>>>({});

  const load = useCallback(async () => {
    if (!jwtIsAdmin) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const data = await apiJson<QcConfig>('/api/admin/qc-config');
      setConfig(data);
    } catch (err) {
      if (isApiError(err) && err.status === 403) {
        setForbidden(true);
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load QC config');
    } finally {
      setLoading(false);
    }
  }, [jwtIsAdmin]);

  useEffect(() => { void load(); }, [load]);

  const flip = async (key: GateKey) => {
    if (!config || busy) return;
    const next = !config[key].effective;
    const previous = config;
    setBusy(key);
    setRowError(prev => ({ ...prev, [key]: undefined }));
    setConfig({
      ...config,
      [key]: { setting: next, effective: next }
    });
    try {
      const data = await apiJson<QcConfig>('/api/admin/qc-config', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ [GATES[key].patchKey]: next })
      });
      setConfig(data);
    } catch (err) {
      setConfig(previous);
      const msg = err instanceof Error ? err.message : `HTTP ${isApiError(err) ? err.status : '?'}`;
      setRowError(prev => ({ ...prev, [key]: msg }));
      toast({
        title: `Could not update ${GATES[key].label}`,
        description: msg,
        status: 'error',
        duration: 6000
      });
    } finally {
      setBusy(null);
    }
  };

  if (forbidden) {
    return (
      <VStack align="stretch" spacing={6}>
        <PageHeader
          eyebrow="Platform"
          title="Admin"
          description="Platform-wide controls. Super-admins only."
        />
        <Card variant="outline">
          <CardBody>
            <VStack align="stretch" spacing={3} py={6} textAlign="center">
              <Text fontSize="sm" color="brand.muted" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">
                You don&apos;t have access
              </Text>
              <Text color="brand.ink" fontWeight="600" fontSize="lg">
                This page is limited to platform super-admins.
              </Text>
              <Text fontSize="sm" color="brand.muted" maxW="520px" mx="auto">
                If you believe you should have access, sign out and back in so your session picks up a fresh admin flag, or ask an existing super-admin to promote you.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <PageHeader
        eyebrow="Platform"
        title="Admin"
        description="Platform-wide controls. These gates apply to every workspace — not just the one currently selected."
      />

      <Card variant="outline">
        <CardBody>
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm" color="brand.muted" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">
              Vision QC
            </Text>
            <Text fontSize="sm" color="brand.muted">
              Turning a gate on starts a billable vision call per render. A change takes effect within ~5s on all services.
            </Text>

            {loading ? (
              <VStack align="stretch" spacing={3} pt={2}>
                <Skeleton height="88px" borderRadius="md" />
                <Skeleton height="88px" borderRadius="md" />
              </VStack>
            ) : error ? (
              <Text color="red.600" fontSize="sm" pt={2}>{error}</Text>
            ) : config ? (
              <VStack align="stretch" spacing={3} pt={2}>
                <GateToggle
                  gateKey="static"
                  checked={config.static.effective}
                  busy={busy === 'static'}
                  error={rowError.static}
                  onToggle={() => void flip('static')}
                />
                <GateToggle
                  gateKey="video"
                  checked={config.video.effective}
                  busy={busy === 'video'}
                  error={rowError.video}
                  onToggle={() => void flip('video')}
                />
              </VStack>
            ) : null}
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
}

function GateToggle({
  gateKey, checked, busy, error, onToggle
}: {
  gateKey: GateKey;
  checked: boolean;
  busy: boolean;
  error?: string;
  onToggle: () => void;
}) {
  const meta = GATES[gateKey];
  return (
    <HStack
      align="flex-start"
      spacing={3}
      p={3}
      borderRadius="md"
      borderWidth="1px"
      borderColor={error ? 'red.200' : 'brand.border'}
      bg={checked ? 'rgba(16,185,129,0.04)' : 'brand.surface'}
    >
      <Switch
        isChecked={checked}
        onChange={onToggle}
        isDisabled={busy}
        colorScheme="green"
        mt={0.5}
      />
      <Box flex={1} minW={0}>
        <HStack spacing={2} mb={1}>
          <Text fontSize="sm" fontWeight="700" color="brand.ink">{meta.label}</Text>
          <Badge
            variant="subtle"
            colorScheme={checked ? 'green' : 'gray'}
            fontSize="9px"
            textTransform="uppercase"
          >
            {checked ? 'On' : 'Off'}
          </Badge>
        </HStack>
        <Text fontSize="xs" color="brand.muted" lineHeight="1.4">{meta.description}</Text>
        {error && (
          <Text fontSize="xs" color="red.600" mt={2}>{error}</Text>
        )}
      </Box>
    </HStack>
  );
}
